import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { getUsers } from '../../../api/UsersAPI';
import { getExpenseHeads } from '../../../api/HeadOfAccountsAPI';
import { createClubExpense, fetchClubExpense, updateClubExpense } from '../../../api/ClubExpensesAPI';
import { handleApiError } from '../../../api/risapi';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppToaster from '../../../components/AppToaster';
import { capitalizeFirstLetter } from '../../../commonfunctions';


const ClubExpenseForm = ({ formType }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = formType === 'edit';

    const [expense, setExpense] = useState({
        expense_date: new Date().toISOString().slice(0, 10),
        head_of_account_id: null,
        description: '',
        total_amount: '',
    });
    const [members, setMembers] = useState([]);
    const [headsOfAccount, setHeadsOfAccount] = useState([]);
    const [selectedContributors, setSelectedContributors] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    // --- State for the 'Select All' checkbox ---
    const [isAllSelected, setIsAllSelected] = useState(false);

    useEffect(() => {
        const loadPrerequisites = async () => {
            // Fetch both members and expense heads of account
            const [headsRes, usersRes] = await Promise.all([
                new Promise(r => getExpenseHeads(r)),
                new Promise(r => getUsers({ paginate: false }, r))
            ]);

            if(headsRes.expense_heads) setHeadsOfAccount(headsRes.expense_heads);
            if (usersRes.users) setMembers(usersRes.users.data || usersRes.users);

            if (isEditMode && id) {
                const expenseRes = await new Promise(r => fetchClubExpense(id, r));
                if (expenseRes.expense) {
                    const fetchedExpense = expenseRes.expense;
                    setExpense({
                        expense_date: fetchedExpense.expense_date,
                        head_of_account_id: fetchedExpense.head_of_account_id,
                        description: fetchedExpense.description,
                        total_amount: fetchedExpense.total_amount,
                    });
                    const contributorIds = fetchedExpense.contributions.map(c => c.user_id);
                    setSelectedContributors(new Set(contributorIds));
                }
            }
            setIsLoading(false);
        };
        loadPrerequisites();
    }, [id, isEditMode]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setExpense(prev => ({ ...prev, [name]: value }));
    };
    
    const handleHeadChange = (selectedOption) => {
        setExpense(prev => ({ ...prev, head_of_account_id: selectedOption ? selectedOption.value : null }));
    };

    const handleContributorChange = (userId, isChecked) => {
        setSelectedContributors(prev => {
            const newSelected = new Set(prev);
            if (isChecked) newSelected.add(userId);
            else newSelected.delete(userId);
            return newSelected;
        });
    };

    // --- Handler for the 'Select All' checkbox ---
    const handleSelectAllChange = (event) => {
        const isChecked = event.target.checked;
        setIsAllSelected(isChecked);
        if (isChecked) {
            // Select all member IDs
            setSelectedContributors(new Set(members.map(member => member.id)));
        } else {
            // Deselect all
            setSelectedContributors(new Set());
        }
    };

    useEffect(() => {
        // Check only if members have loaded
        if (members.length > 0 && selectedContributors.size === members.length) {
            setIsAllSelected(true);
        } else {
            setIsAllSelected(false);
        }
    }, [selectedContributors, members]);

    const handleSubmit = (exitForm) => {
        setIsSaving(true);
        setErrors({});
        const dataToSend = { ...expense, contributor_ids: Array.from(selectedContributors) };
        
        const apiCall = isEditMode ? updateClubExpense : createClubExpense;
        apiCall({ ...dataToSend, id }, (response) => {
            setIsSaving(false);
            if (response.expense) {
                const message = `Expense ${isEditMode ? 'updated' : 'created'} successfully!`;
                if (exitForm) {
                    navigate('/club_portal/game-finances', { state: { message } });
                } else {
                    setToaster({ type: 'success', heading: 'Success', message });
                    setShowToaster(true);
                    // Reset form for next entry
                    setExpense({ expense_date: new Date().toISOString().slice(0, 10), head_of_account_id: null, description: '', total_amount: '' });
                    setSelectedContributors(new Set());
                }
            } else {
                handleApiError(response, setErrors);
            }
        });
    };
    
    const sharePerPerson = selectedContributors.size > 0 && expense.total_amount > 0
        ? (expense.total_amount / selectedContributors.size).toFixed(2)
        : '0.00';
    
    const headOptions = headsOfAccount.map(h => ({ value: h.id, label: h.name }));

    if (isLoading) return <div className="text-center p-5"><div className="spinner-border"></div></div>;

    return (
        <main>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title={isEditMode ? "Edit Club Expense" : "Record New Club Expense"}>
                    <li><a className="f-s-14 f-w-500" href="#">Financials</a></li>
                    <li><a className="f-s-14 f-w-500" href="/club_portal/financials/expenses">Club Expenses</a></li>
                    <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                </AppBreadCrumb>
                <div className="card">
                    <div className="card-header"><h5>Expense Details</h5></div>
                    <div className="card-body">
                        <form className="app-form">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Expense Date *</label>
                                    <input type="date" className="form-control" name="expense_date" value={expense.expense_date} onChange={handleInputChange} />
                                    {errors.expense_date && <div className="text-danger small">{errors.expense_date}</div>}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Total Amount (₹) *</label>
                                    <input type="number" className="form-control" name="total_amount" value={expense.total_amount} onChange={handleInputChange} placeholder="e.g., 1500" />
                                    {errors.total_amount && <div className="text-danger small">{errors.total_amount}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                     <label className="form-label">Head of Account *</label>
                                     <Select options={headOptions} value={headOptions.find(o => o.value === expense.head_of_account_id)} onChange={handleHeadChange} />
                                     {errors.head_of_account_id && <div className="text-danger small">{errors.head_of_account_id}</div>}
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label">Description *</label>
                                    <input type="text" className="form-control" name="description" value={expense.description} onChange={handleInputChange} placeholder="e.g., Purchase of new SG cricket balls" />
                                    {errors.description && <div className="text-danger small">{errors.description}</div>}
                                </div>
                            </div>
                            <hr/>
                            <h5>Select Contributing Members</h5>
                            <div className="alert alert-info mt-2">Share Per Person: <strong>₹{sharePerPerson}</strong></div>
                            {/* --- ADDED SELECT ALL CHECKBOX --- */}
                            {members.length > 0 && (
                                <div className="form-check form-check-inline border-bottom pb-2 mb-2 w-100">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="selectAllMembers"
                                        checked={isAllSelected}
                                        onChange={handleSelectAllChange}
                                        disabled={isLoading}
                                    />
                                    <label className="form-check-label fw-bold" htmlFor="selectAllMembers">
                                        Select All / Deselect All ({members.length})
                                    </label>
                                </div>
                            )}
                            {/* --- END SELECT ALL --- */}
                            
                            <div className="permission-grid mt-3">
                                {members.map(member => (
                                    <div key={member.id} className="form-check">
                                        <input className="form-check-input" type="checkbox" id={`member-${member.id}`} checked={selectedContributors.has(member.id)} onChange={(e) => handleContributorChange(member.id, e.target.checked)} />
                                        <label className="form-check-label" htmlFor={`member-${member.id}`}>{member.name}</label>
                                    </div>
                                ))}
                            </div>
                             {errors.contributor_ids && <div className="text-danger small mt-2">{errors.contributor_ids}</div>}
                        </form>
                    </div>
                    <div className="card-footer text-end">
                        {!isEditMode ? (
                            <>
                                <button type="button" className="btn btn-outline-primary me-2 btn-sm" onClick={() => handleSubmit(false)}>Save + Next</button>
                                <button type="button" className="btn btn-primary me-2 btn-sm" onClick={() => handleSubmit(true)}>Save + Exit</button>
                            </>
                        ) : (
                            <>
                                <button type="button" className="btn btn-primary me-2 btn-sm" onClick={() => handleSubmit(true)} disabled={isSaving}>{isSaving ? 'Updating...' : 'Update Expense'}</button>
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => navigate('/club_portal/financials/expenses')}>Cancel</button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <style>{`.permission-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; }`}</style>
        </main>
    );
};

export default ClubExpenseForm;

